<div class="question-creation-wrapper">
  <!-- Header Section -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">
        <i class="bi bi-clipboard-check"></i>
        Create Customer Survey
      </h1>
      <p class="page-subtitle">Design comprehensive questionnaires for customer feedback</p>
    </div>
    <div class="header-actions">
      <button class="btn btn-preview">
        <i class="bi bi-eye"></i> Show Preview
      </button>

      <button
        type="button"
        class="btn btn-reset"
        (click)="resetForm()">
        <i class="bi bi-arrow-counterclockwise"></i>
        Reset Form
      </button>
    </div>
  </div>

  <!-- Main Form -->
  <form [formGroup]="surveyForm" (ngSubmit)="onSubmit()" class="survey-form">
    
    <!-- Survey Basic Information Card -->
    <div class="form-card survey-info-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="bi bi-info-circle"></i>
          Survey Information
        </h3>
      </div>
      <div class="card-body">
        <div class="row">
          <!-- Survey Title -->
          <div class="col-md-6">
            <div class="form-group">
              <label for="survey_title" class="form-label required">
                Survey Title
              </label>
              <input
                type="text"
                id="survey_title"
                class="form-control"
                formControlName="survey_title"
                placeholder="e.g., Customer Satisfaction Survey Q1 2026"
                [class.is-invalid]="isFieldInvalid('survey_title')"
              />
              <div class="invalid-feedback" *ngIf="isFieldInvalid('survey_title')">
                <span *ngIf="surveyForm.get('survey_title')?.errors?.['required']">
                  Survey title is required
                </span>
                <span *ngIf="surveyForm.get('survey_title')?.errors?.['minlength']">
                  Title must be at least 3 characters long
                </span>
              </div>
            </div>
          </div>

          <!-- Survey Description -->
          <div class="col-md-6">
            <div class="form-group">
              <label for="survey_description" class="form-label required">
                Survey Description
              </label>
              <input
                type="text"
                id="survey_description"
                class="form-control"
                formControlName="survey_description"
                placeholder="e.g., Quarterly customer satisfaction assessment"
                [class.is-invalid]="isFieldInvalid('survey_description')"
              />
              <div class="invalid-feedback" *ngIf="isFieldInvalid('survey_description')">
                <span *ngIf="surveyForm.get('survey_description')?.errors?.['required']">
                  Description is required
                </span>
                <span *ngIf="surveyForm.get('survey_description')?.errors?.['minlength']">
                  Description must be at least 10 characters long
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Questionnaire Sets -->
    <div formArrayName="questionnaire_sets" class="questionnaire-sets">
      <div
        *ngFor="let setControl of questionnaireSets.controls; let setIndex = index"
        class="form-card questionnaire-set-card"
        [formGroupName]="setIndex"
      >
        <!-- Set Header -->
        <div class="card-header set-header">
          <div class="set-header-left">
            <span class="set-badge">Section {{ setIndex + 1 }}</span>
            <div class="set-name-input-wrapper">
              <input
                type="text"
                class="form-control set-name-input"
                formControlName="set_name"
                placeholder="Enter section name (e.g., Product Quality)"
                [class.is-invalid]="setControl.get('set_name')?.invalid && setControl.get('set_name')?.touched"
              />
            </div>
          </div>
          <button
            type="button"
            class="btn btn-danger btn-sm remove-set-btn"
            (click)="removeQuestionnaireSet(setIndex)"
            [disabled]="questionnaireSets.length === 1"
            title="Remove section">
            <i class="bi bi-trash"></i>
            Remove Section
          </button>
        </div>

        <!-- Questions in this Set -->
        <div class="card-body">
          <div formArrayName="questions" class="questions-container">
            <div
              *ngFor="let questionControl of getQuestions(setIndex).controls; let qIndex = index"
              class="question-card"
              [formGroupName]="qIndex"
            >
              <!-- Question Header -->
              <div class="question-header">
                <div class="question-number">
                  <i class="bi bi-question-circle"></i>
                  Question {{ qIndex + 1 }}
                </div>
                <div class="question-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-icon"
                    (click)="moveQuestionUp(setIndex, qIndex)"
                    [disabled]="qIndex === 0"
                    title="Move up">
                    <i class="bi bi-arrow-up"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-icon"
                    (click)="moveQuestionDown(setIndex, qIndex)"
                    [disabled]="qIndex === getQuestions(setIndex).length - 1"
                    title="Move down">
                    <i class="bi bi-arrow-down"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-icon"
                    (click)="duplicateQuestion(setIndex, qIndex)"
                    title="Duplicate">
                    <i class="bi bi-files"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-icon btn-danger"
                    (click)="removeQuestion(setIndex, qIndex)"
                    [disabled]="getQuestions(setIndex).length === 1"
                    title="Delete">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>

              <!-- Question Content -->
              <div class="question-content">
                <div class="row">
                  <!-- Question Text -->
                  <div class="col-md-8">
                    <div class="form-group">
                      <label class="form-label required">Question Text</label>
                      <textarea
                        class="form-control question-textarea"
                        formControlName="question_text"
                        rows="3"
                        placeholder="Enter your question here..."
                        [class.is-invalid]="isQuestionFieldInvalid(setIndex, qIndex, 'question_text')"
                      ></textarea>
                      <div class="invalid-feedback" *ngIf="isQuestionFieldInvalid(setIndex, qIndex, 'question_text')">
                        Question text is required (minimum 5 characters)
                      </div>
                    </div>
                  </div>

                  <!-- Question Type -->
                  <div class="col-md-4">
                    <div class="form-group">
                      <label class="form-label required">Question Type</label>
                      <select
                        class="form-select question-type-select"
                        formControlName="question_type"
                        (change)="onQuestionTypeChange(setIndex, qIndex)"
                      >
                        <option *ngFor="let type of questionTypes" [value]="type.value">
                          {{ type.icon }} {{ type.label }}
                        </option>
                      </select>
                    </div>

                    <!-- Required Toggle -->
                    <div class="form-group mt-3">
                      <div class="form-check form-switch">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          formControlName="is_required"
                          [id]="'required_' + setIndex + '_' + qIndex"
                        />
                        <label class="form-check-label" [for]="'required_' + setIndex + '_' + qIndex">
                          <i class="bi bi-asterisk text-danger"></i>
                          Required Question
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Options Section (for multiple choice, checkbox, yes/no) -->
                <div *ngIf="needsOptions(setIndex, qIndex)" class="options-section">
                  <div class="options-header">
                    <label class="form-label required">
                      <i class="bi bi-list-ul"></i>
                      Answer Options
                    </label>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      (click)="addOption(setIndex, qIndex)">
                      <i class="bi bi-plus-circle"></i>
                      Add Option
                    </button>
                  </div>

                  <div formArrayName="options" class="options-list">
                    <div
                      *ngFor="let option of getOptions(setIndex, qIndex).controls; let optIndex = index"
                      class="option-item"
                    >
                      <span class="option-number">{{ optIndex + 1 }}.</span>
                      <input
                        type="text"
                        class="form-control option-input"
                        [formControlName]="optIndex"
                        placeholder="Enter option text"
                        [class.is-invalid]="option.invalid && option.touched"
                      />
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger remove-option-btn"
                        (click)="removeOption(setIndex, qIndex, optIndex)"
                        [disabled]="getOptions(setIndex, qIndex).length <= 2"
                        title="Remove option">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Yes/No Options Display -->
                <div *ngIf="getQuestionControl(setIndex, qIndex, 'question_type')?.value === 'yes_no'" 
                     class="yes-no-display">
                  <div class="alert alert-info">
                    <i class="bi bi-info-circle"></i>
                    This question will have predefined Yes/No options
                  </div>
                </div>

                <!-- Star Rating Display -->
                <div *ngIf="getQuestionControl(setIndex, qIndex, 'question_type')?.value === 'star_rating'" 
                     class="star-rating-display">
                  <div class="alert alert-info">
                    <i class="bi bi-star-fill"></i>
                    Users will rate using a 5-star scale
                  </div>
                </div>
              </div>
            </div>

            <!-- Add Question Button -->
            <div class="add-question-section">
              <button
                type="button"
                class="btn btn-outline-primary add-question-btn"
                (click)="addQuestion(setIndex)">
                <i class="bi bi-plus-circle"></i>
                Add Question to This Section
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add New Section Button -->
    <div class="add-section-wrapper">
      <button
        type="button"
        class="btn add-section-btn"
        (click)="addQuestionnaireSet()">
        <i class="bi bi-plus-lg"></i> + Add Section
      </button>
    </div>

    <!-- Form Actions -->
    <div class="form-actions">

      <button
        type="submit"
        class="btn btn-lg btn-primary"
        [disabled]="surveyForm.invalid || isSubmitting">
        <span *ngIf="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!isSubmitting" class="bi bi-check-circle"></i>
        {{ isSubmitting ? 'Creating Survey...' : 'Create Survey' }}
      </button>
    </div>
  </form>

  <!-- Preview Section (Optional) -->
  <div *ngIf="showPreview && surveyForm.valid" class="preview-section">
    <div class="preview-card">
      <div class="preview-header">
        <h3><i class="bi bi-eye"></i> Survey Preview</h3>
        <button type="button" class="btn btn-sm btn-close" (click)="togglePreview()"></button>
      </div>
      <div class="preview-body">
        <pre>{{ surveyForm.value | json }}</pre>
      </div>
    </div>
  </div>
</div>